import { useCallback, useEffect, useMemo, useState } from 'react';

import { styled } from '@material-ui/core';

import { Node, mergeAttributes } from '@tiptap/core';
import {
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from '@tiptap/react';

import {
  useBlockZoneId,
  useIsEditorEditable,
} from '@spotify/confidence-editor-react';
import {
  blockIdAttribute,
  generateBlockId,
} from '@spotify/confidence-editor-react';
import {
  tiptapBlockMarginBottom,
  tiptapBlockMarginTop,
} from '@spotify/confidence-editor-react';
import { BaseStyledTable } from '@spotify/confidence-editor-react';
import { CommentZone } from '@spotify/plugin-confidence-comments-react';

import { isMetricStale } from '../../metrics';
import { useReportBlockData } from '../ReportBlockDataContext';
import { useReportState } from '../ReportHeader/useReportState';
import { StaleMetricBadge } from '../StaleMetricBadge';
import { MetricTableRow } from './MetricTableRow';
import { MetricTableToolbar } from './MetricTableToolbar';
import { TableHead } from './TableHead';
import { createMetricRow } from './helpers';
import { MetricRow } from './types';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    metricTable: {
      setMetricTable: () => ReturnType;
    };
  }
}

const MetricTableContainer = styled('div')({
  position: 'relative',
  width: '100%',
  '& .metric-table-hover-toolbar': {
    opacity: 0,
    pointerEvents: 'none' as const,
  },
  '&:hover .metric-table-hover-toolbar': {
    opacity: 1,
    pointerEvents: 'auto' as const,
  },
});

const HoverToolbar = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'translateY(-100%)',
  paddingBottom: 6,
  zIndex: 1000,
});

const ScrollableTableWrapper = styled('div')({
  overflowX: 'auto',
  width: '100%',
});

const StyledMetricTable = styled(BaseStyledTable)({
  backgroundColor: 'transparent',
  border: 'none',
  marginBottom: tiptapBlockMarginBottom,
  marginTop: tiptapBlockMarginTop,

  '& td': {
    backgroundColor: 'transparent',
    verticalAlign: 'middle',
    height: '100%',

    '& > p': {
      display: 'flex',
      alignItems: 'center',
      minHeight: '40px',
      height: '100%',
    },

    '&:first-child': {
      '& > p': {
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
      },
    },
  },

  '& th': {
    backgroundColor: 'transparent',
    verticalAlign: 'middle',

    '& > p': {
      display: 'flex',
      alignItems: 'center',
      minHeight: '40px',
      height: '100%',
      margin: 0,
    },
  },
});

const MetricTableComponent = ({
  node,
  updateAttributes,
  deleteNode,
  editor,
  getPos,
}: NodeViewProps) => {
  const [initialized, setInitialized] = useState(false);
  const isEditable = useIsEditorEditable(editor);
  const { availableMetrics } = useReportBlockData();
  const { isPublished } = useReportState();

  const showStaleIndicator = !isPublished;

  const { blockId } = node.attrs;
  const zoneId = useBlockZoneId('metric-table', blockId, updateAttributes);

  const storedRows: MetricRow[] = node.attrs.rows || [];

  // Use stored rows if available, otherwise use all available metrics
  const rows: MetricRow[] =
    storedRows.length > 0 ? storedRows : availableMetrics.map(createMetricRow);

  // Initialize with all available metrics on first render
  useEffect(() => {
    if (
      !initialized &&
      storedRows.length === 0 &&
      availableMetrics.length > 0
    ) {
      const initialRows = availableMetrics.map(createMetricRow);
      updateAttributes({ rows: initialRows });
      setInitialized(true);
    }
  }, [initialized, storedRows.length, availableMetrics, updateAttributes]);

  const staleMetricNames = useMemo(
    () =>
      rows
        .filter(row => {
          const am = availableMetrics.find(m => m.id === row.metricId);
          return am ? isMetricStale(row, am) : false;
        })
        .map(row => row.name),
    [rows, availableMetrics],
  );

  const handleRefreshAll = useCallback(() => {
    const refreshedRows = rows.map(row => {
      const am = availableMetrics.find(m => m.id === row.metricId);
      return am ? createMetricRow(am) : row;
    });
    updateAttributes({ rows: refreshedRows });
  }, [rows, availableMetrics, updateAttributes]);

  const content = (
    <MetricTableContainer>
      {isEditable && (
        <HoverToolbar
          contentEditable={false}
          className="metric-table-hover-toolbar"
        >
          <MetricTableToolbar
            node={node}
            deleteNode={deleteNode}
            updateAttributes={updateAttributes}
            metrics={availableMetrics}
            editor={editor}
            getPos={getPos}
            isStale={showStaleIndicator && staleMetricNames.length > 0}
            onRefresh={handleRefreshAll}
          />
        </HoverToolbar>
      )}
      {showStaleIndicator && staleMetricNames.length > 0 && (
        <StaleMetricBadge
          tooltip={`Metric data for ${staleMetricNames.join(', ')} has changed since this was added`}
        />
      )}
      <ScrollableTableWrapper>
        <StyledMetricTable>
          <TableHead />
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <p
                    style={{
                      color: 'var(--text-subdued)',
                    }}
                  >
                    No metrics available
                  </p>
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <MetricTableRow key={row.metricId} row={row} index={index} />
              ))
            )}
          </tbody>
        </StyledMetricTable>
      </ScrollableTableWrapper>
    </MetricTableContainer>
  );

  return (
    <NodeViewWrapper>
      {zoneId ? (
        <CommentZone id={zoneId} component="div">
          {content}
        </CommentZone>
      ) : (
        content
      )}
    </NodeViewWrapper>
  );
};

export const MetricTable = Node.create({
  name: 'metricTable',
  group: 'block',
  atom: true,
  defining: true,

  addAttributes() {
    return {
      rows: {
        default: [],
        parseHTML: element => {
          const rowsAttr = element.getAttribute('data-rows');
          return rowsAttr ? JSON.parse(rowsAttr) : [];
        },
        renderHTML: attributes => ({
          'data-rows': JSON.stringify(attributes.rows),
        }),
      },
      blockId: blockIdAttribute,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="metric-table"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'metric-table' }),
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(MetricTableComponent);
  },

  addCommands() {
    return {
      setMetricTable:
        () =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { blockId: generateBlockId() },
          });
        },
    };
  },
});
